import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from '../../util/SnackbarProvider';
import TaskEditorTemplate from "./TaskEditorTemplate";
import userService from "../../services/UserService";
import taskService from "../../services/TaskService";
import {toString} from "../../util/DateConverter";
import dayjs from 'dayjs';
import vehicleService from "../../services/VehicleService";
import projectService from "../../services/ProjectService";
import projectTaskService from "../../services/ProjectTaskService";
import cloudStorageFileService from "../../services/CloudStorageFileService";
import authService from "../../services/AuthService";
import axios from "axios";
import {API_URL} from "../../App";

const TaskEditor = () => {

    const urlParts = window.location.pathname.split('/')
    const id = urlParts[3] === 'create' ? undefined : urlParts[3]

    useTitle([
        {name: 'Feladat Lista', url: '/dashboard/tasks'},
        {name: 'Feladat', url: '/dashboard/tasks/' + (id || 'create')},
    ])

    const [task, setTask] = useState({status: 'NOT_STARTED'});
    const {sendAlertMessage} = useSnackbar()
    const [waitingForServer, setWaitingForServer] = useState(false)
    const [message, setMessage] = useState('')
    const navigate = useNavigate()
    const [chatMessage, setChatMessage] = useState('')

    useEffect(() => {
        if (!id) return
        retrieveTask()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    function retrieveTask() {
        taskService.findById(id)
            .then(response => {
                if(response.projectTask != null){
                    projectTaskService.findById(response.projectTask.id)
                    .then(projectTask => setTask((prev) => ({...prev, project: projectTask.project})))
                }
                setTask((prev) => ({
                    ...prev,
                    ...response,
                    deadline: response.deadline ? dayjs(response.deadline) : null,
                    scheduleDateStart: response.scheduleDateStart ? dayjs(response.scheduleDateStart) : null,
                    scheduleDateEnd: response.scheduleDateEnd ? dayjs(response.scheduleDateEnd) : null,
                }))
            })
            .catch(error => sendAlertMessage(error?.message || 'Feladat lekérése sikertelen', 'error'))
    }

    const onChange = (e, v, n) => {
        const name = n || e.target.name
        const value = v || e.target.value
        setTask((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const onChangeCheckBox = (event) => {
        const {name, checked} = event.target;
        setTask((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const onDateTimeChange = (newValue, name) => {
        setTask((prev) => ({
            ...prev,
            [name]: newValue,
        }));
    };

    const onChangeChatMessage = (event) => {
        setChatMessage(event.target.value);
    };

    const [users, setUsers] = useState([]);
    useEffect(() => {
        userService.getSelectors()
            .then(response => setUsers(response))
            .catch(error => console.error(error))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [clients, setClients] = useState([]);
    useEffect(() => {
        userService.getClientSelectors({onlyClients: true})
            .then(response => setClients(response))
            .catch(error => console.error(error))
    }, []);

    const [vehicles, setVehicles] = useState([]);
    useEffect(() => {
        vehicleService.getSelectors()
            .then(response => setVehicles(response))
            .catch(error => console.error(error))
    }, []);

    const [projects, setProjects] = useState([]);
    useEffect(() => {
        projectService.getSelectors()
            .then(response => setProjects(response))
            .catch(error => console.error(error))
    }, []);

    const [projectTasks, setProjectTasks] = useState(null);
    useEffect(() => {
        if (projects == null || task?.project == null) return
        projectTaskService.search(task.project.id, {size: 100})
            .then(response => setProjectTasks(response.content))
            .catch(error => console.error(error))
    }, [task?.project]);

    const create = async (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(prepareRequest())], {type: "application/json"}));
        files.forEach((file) => {
            formData.append('files', file);
        });

        const headers = {'Authorization': 'Bearer ' + authService.getCurrentUser().accessToken};
        try {
            const response = await axios.post(API_URL + '/tasks', formData, {headers: headers});
            if (response) {
                sendAlertMessage('Feladat sikeresen létrehozva')
                navigate(-1);
            }
        } catch (error) {
            sendAlertMessage(error?.response?.data?.message || 'Feladat létrehozása sikertelen', 'error')
        }
        setWaitingForServer(false)
    };

    const update = async (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(prepareRequest())], {type: "application/json"}));
        files.forEach((file) => {
            formData.append('files', file);
        });

        const headers = {'Authorization': 'Bearer ' + authService.getCurrentUser().accessToken};
        try {
            const response = await axios.patch(API_URL + '/tasks/' + id, formData, {headers: headers});
            if (response) {
                sendAlertMessage('Feladat sikeresen mentve')
                retrieveTask()
                setFiles([])
            }
        } catch (error) {
            sendAlertMessage(error?.response?.data?.message || 'Alfeladat mentése sikertelen', 'error')
        }
        setWaitingForServer(false)
    };

    const prepareRequest = () => {
        const copy = structuredClone(task)
        copy.deadline = toString(copy.deadline)
        copy.scheduleDateStart = toString(copy.scheduleDateStart)
        copy.scheduleDateEnd = toString(copy.scheduleDateEnd)
        return copy
    }

    const onUsersChange = (event, type) => {
        const {value} = event.target
        const newSelection = typeof value === 'string' ? value.split(',') : value;
        const uniqueSelection = newSelection.reduce((acc, user) => {
            const exists = acc.find(item => item.id === user.id);
            if (exists) return acc.filter(item => item.id !== user.id);
            else return [...acc, user];
        }, []);

        setTask((prev) => ({
            ...prev,
            [type]: uniqueSelection,
        }));
    };

    const onChangeSelectItem = (newValue, name) => {
        setTask((prev) => ({
            ...prev,
            [name]: newValue
        }));
    }

    const removeDocumentName = (fileId) => {
        taskService.deleteFile(id, fileId)
            .then(() => {
                retrieveTask()
                sendAlertMessage('Fájl sikeresen törölve!')
            })
            .catch(error => sendAlertMessage(error.message, 'error'))
    };

    const updateFileDescription = (id, form) => {
        cloudStorageFileService.update(id, form)
            .then(() => {
                retrieveTask()
                sendAlertMessage('Fájl leírása sikeresen módosítva!')
            })
            .catch(error => sendAlertMessage(error.message, 'error'))
    }

    const [files, setFiles] = useState([]);
    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    return (
        <TaskEditorTemplate
            {...{
                id, task, message, onChange, create, update, navigate, waitingForServer, onUsersChange, users, clients, chatMessage, sendAlertMessage,
                onChangeChatMessage, onChangeCheckBox, onDateTimeChange, vehicles, onChangeSelectItem, projectTasks, projects,
                removeDocumentName, updateFileDescription, handleFileChange, handleRemoveFile, files
            }}
        />
    );
};

export default TaskEditor;
